import { IconButton, Spinner, XLineIcon } from "@relex-solutions/fragments"
import type { ReactElement } from "react"
import { cloneElement, useId, useRef } from "react"
import styles from "./SearchInputWithOptions.module.css"

export interface SearchInputWithOptionsOption {
  id: string
  label: string
  disabled?: boolean
  selected?: boolean
  rightIcon?: ReactElement | null
}

export interface SearchInputWithOptionsProps {
  value: string
  label: string
  placeholder?: string
  disabled?: boolean
  showResults: boolean
  options: SearchInputWithOptionsOption[]
  loading?: boolean
  resultsAriaLabel?: string
  clearAriaLabel?: string
  containerClass?: string
  onChange: (value: string) => void
  onClear?: () => void
  onSelect: (option: SearchInputWithOptionsOption) => void
}

const getRightIcon = (option: SearchInputWithOptionsOption) => {
  return option.rightIcon ? cloneElement(option.rightIcon, { className: styles.rightIcon, "aria-hidden": true }) : null
}

export function SearchInputWithOptions({
  value,
  placeholder,
  disabled,
  showResults,
  options,
  loading = false,
  resultsAriaLabel,
  clearAriaLabel = "clear",
  label,
  containerClass,
  onChange,
  onClear,
  onSelect
}: SearchInputWithOptionsProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const resultsId = `${inputId}-results`

  return (
    <div className={containerClass}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      <div className={styles.wrapper}>
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            id={inputId}
            className={styles.input}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            role="combobox"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-controls={resultsId}
            aria-expanded={showResults}
          />
          {loading && <Spinner size="small" className={styles.loadingIcon} />}
          {!loading && value.length > 0 && onClear && (
            <IconButton
              className={styles.clearButton}
              aria-label={clearAriaLabel}
              size="small"
              variant="ghost"
              onClick={() => {
                onClear()
                inputRef?.current?.focus()
              }}
              borderRadius="full"
            >
              <XLineIcon />
            </IconButton>
          )}
        </div>

        {showResults && options.length > 0 && (
          <ul
            id={resultsId}
            className={styles.listbox}
            aria-label={resultsAriaLabel}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Following W3C ARIA practices guide for combobox pattern
            role="listbox"
          >
            {options.map((option) => {
              const rightAdornment = getRightIcon(option)
              return (
                <li
                  key={option.id}
                  // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: Following W3C ARIA practices guide for combobox pattern
                  role="option"
                  tabIndex={option.disabled ? -1 : 0}
                  aria-selected={!!option.selected}
                  aria-disabled={!!option.disabled}
                  className={option.disabled ? styles.itemDisabled : styles.item}
                  onClick={
                    option.disabled
                      ? undefined
                      : () => {
                          onSelect(option)
                          inputRef?.current?.focus()
                        }
                  }
                  onKeyDown={(e) => {
                    if (option.disabled) return
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      onSelect(option)
                      inputRef?.current?.focus()
                    }
                  }}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {rightAdornment}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
